import { hashWords } from "daedalus-core";
import { fullName, isNameCharacter } from "../names/character-name.ts";

/**
 * Who is online, the shared half (ADR-056's amendment of 2026-10-06, decisions 1 to 3 and 5, with the staff types of
 * the amendment that follows it): the listed player and the answer of `GET /moderation/online`, the page size and
 * the pages a page shows, the search rule, the list's order and the page cursor. The gateway builds the ordered list
 * and slices it; the page reads the answer. Names are compared by their lower case and then by UTF-16 code units,
 * never by a locale's collation, which differs between engines and locales: `toLowerCase` is the locale-free
 * Unicode mapping and the `<` of two strings is code-unit order, both fixed by the language, so the gateway and every
 * page order a list the same way.
 */

/**
 * The most players one answer holds, and the most a caller may ask for.
 */
export const ONLINE_PAGE_SIZE = 50;

/**
 * The most pages a page shows (200 rows) before it asks the GM to search.
 */
export const ONLINE_PAGES_SHOWN = 4;

/**
 * The longest query: two parts of a name at their cap of 12 and the space between them.
 */
export const ONLINE_QUERY_MAX_CHARS = 25;

/**
 * One player in the list. Keyed by its names; it never holds an email, an address, any id, a role's name or a
 * standing's number. `badge` is the staff badge's number (ADR-112 decision 5.2; 0 for a player) and `staff` is true
 * when it is not 0, for a page that reads no badge.
 */
export interface OnlinePlayer {
  readonly first: string;
  readonly family: string;
  readonly map: string;
  readonly you: boolean;
  readonly staff: boolean;
  readonly badge: number;
  readonly badgeHidden: boolean;
  readonly actable: boolean;
  readonly frozen: boolean;
  readonly muted: boolean;
}

/**
 * One map with a match, as a group's header: its id, its name, its region once the manifest carries one, and the
 * count of its players that match the search.
 */
export interface OnlineMap {
  readonly id: string;
  readonly name: string;
  readonly group: string | null;
  readonly count: number;
}

/**
 * The answer of `GET /moderation/online`: the caller's map or null, every map with a match in the list's order, at
 * most `ONLINE_PAGE_SIZE` players, the matches in all and the cursor of the next page or null.
 */
export interface OnlineAnswer {
  readonly own: string | null;
  readonly maps: readonly OnlineMap[];
  readonly players: readonly OnlinePlayer[];
  readonly total: number;
  readonly next: string | null;
}

/**
 * A query held to its rule, in lower case. Only `onlineQuery` makes one.
 */
export type OnlineQuery = string & { readonly onlineQuery: true };

/**
 * A row's place in the list's order: its map's id and name and its names. The cursor is the last row's place.
 */
export interface OnlinePlace {
  readonly mapId: string;
  readonly mapName: string;
  readonly first: string;
  readonly family: string;
}

/**
 * The most characters a cursor may hold; a longer text is refused before it is read.
 */
export const ONLINE_CURSOR_MAX_CHARS = 1024;

/**
 * The seed of the cursor's checksum.
 */
const CURSOR_SEED = 0x6f6e6c69;

/**
 * The version that opens every cursor this module writes.
 */
const CURSOR_VERSION = "1";

/**
 * The separator between a cursor's fields, which `encodeURIComponent` always escapes inside a field.
 */
const CURSOR_SEPARATOR = ",";

/**
 * A cursor's fields: the version, the four of the place, and the checksum.
 */
const CURSOR_FIELDS = 6;

/**
 * A cursor's checksum: eight lower-case hex digits.
 */
const CHECKSUM_PATTERN = /^[0-9a-f]{8}$/;

/**
 * Holds a search to its rule (decision 3, with the wildcard of ADR-056's note of 2026-10-08 on the lookup): 1 to
 * `ONLINE_QUERY_MAX_CHARS` characters, each one `isNameCharacter` admits or a space, with one space at most; and a
 * `*` at the start, the end or both, around at least one other character, and nowhere else. A space may lead or end
 * the text between, and then matches only where the first name and the family name meet: " rook" matches "Mira
 * Rook" and "mira " matches it too.
 *
 * @param text The query as sent.
 * @returns The query in lower case, its `*` kept, or null when the rule refuses it.
 */
export function onlineQuery(text: string): OnlineQuery | null {
  if (text.length < 1 || text.length > ONLINE_QUERY_MAX_CHARS) {
    return null;
  }
  const leads = text.startsWith("*");
  const trails = text.length > 1 && text.endsWith("*");
  const inner = text.slice(leads ? 1 : 0, trails ? -1 : undefined);
  if (inner.length < 1) {
    return null;
  }
  let spaces = 0;
  for (const character of inner) {
    if (character === " ") {
      spaces += 1;
    } else if (!isNameCharacter(character)) {
      return null;
    }
  }
  return spaces > 1 ? null : (text.toLowerCase() as OnlineQuery);
}

/**
 * Whether a player matches a search: without a `*` the query is a run of characters inside the first name, inside
 * the family name or inside "First Family", ignoring case; `pro*` matches where one of the three starts so, `*ant`
 * where one ends so, and `*rov*` as a query with no `*` does.
 *
 * @param query The query held to its rule.
 * @param first The player's first name.
 * @param family The player's family name.
 * @returns True on a match.
 */
export function matchesOnline(query: OnlineQuery, first: string, family: string): boolean {
  const leads = query.startsWith("*");
  const trails = query.length > 1 && query.endsWith("*");
  const inner = query.slice(leads ? 1 : 0, trails ? -1 : undefined);
  const full = fullName(first, family).toLowerCase();
  if (leads === trails) {
    return full.includes(inner);
  }
  return [first.toLowerCase(), family.toLowerCase(), full].some((name) => (trails ? name.startsWith(inner) : name.endsWith(inner)));
}

/**
 * Orders two strings by code unit.
 *
 * @param a The first string.
 * @param b The second string.
 * @returns Negative, zero or positive as `a` comes before, with or after `b`.
 */
function byCodeUnit(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/**
 * Orders two strings ignoring case, then by code unit so that two that differ only by case still have an order.
 *
 * @param a The first string.
 * @param b The second string.
 * @returns Negative, zero or positive as `a` comes before, with or after `b`.
 */
function byName(a: string, b: string): number {
  return byCodeUnit(a.toLowerCase(), b.toLowerCase()) || byCodeUnit(a, b);
}

/**
 * The list's order (decision 2): the caller's map first, the other maps by name ignoring case and then by id, and
 * within a map the players by first name and then family name, ignoring case. A total order when every map id has
 * one name: two places then compare equal only when their four fields are the same. A place whose map name differs
 * from another's of the same id (a cursor from before a map was renamed) is compared with the rows of that map by
 * names alone, while other maps place it by its own name, so the caller sets the place's map name from its id
 * before a search by halving.
 *
 * @param own The caller's map id, or null for a caller with no character in the world.
 * @param a One place.
 * @param b The other place.
 * @returns Negative, zero or positive as `a` comes before, with or after `b`.
 */
export function compareOnline(own: string | null, a: OnlinePlace, b: OnlinePlace): number {
  const aOwn = a.mapId === own;
  const bOwn = b.mapId === own;
  if (aOwn !== bOwn) {
    return aOwn ? -1 : 1;
  }
  return (
    (a.mapId === b.mapId ? 0 : byCodeUnit(a.mapName.toLowerCase(), b.mapName.toLowerCase()) || byCodeUnit(a.mapId, b.mapId)) ||
    byName(a.first, b.first) ||
    byName(a.family, b.family) ||
    byCodeUnit(a.mapName, b.mapName)
  );
}

/**
 * The cursor's checksum over its text: xxHash32 of the text's UTF-16 code units, as eight lower-case hex digits.
 *
 * @param text The cursor's text before its checksum.
 * @returns The checksum.
 */
function checksum(text: string): string {
  const units: number[] = [];
  for (let index = 0; index < text.length; index += 1) {
    units.push(text.charCodeAt(index));
  }
  return hashWords(CURSOR_SEED, units, units.length).toString(16).padStart(8, "0");
}

/**
 * Writes a cursor: the last row's place, each field escaped, and a checksum over the rest. Printable ASCII with no
 * space; not held by the server, so it survives a reconnect and a restart (decision 5). The checksum catches a cursor
 * damaged by accident. It is not a signature: anyone holding this module can write a cursor for any place, and the
 * reader treats the place as untrusted.
 *
 * @param place The last row's place.
 * @returns The cursor's text.
 * @throws URIError when a field holds a lone surrogate.
 */
export function encodeOnlineCursor(place: OnlinePlace): string {
  const body = [CURSOR_VERSION, place.mapId, place.mapName, place.first, place.family].map((field, index) => (index === 0 ? field : encodeURIComponent(field))).join(CURSOR_SEPARATOR);
  return `${body}${CURSOR_SEPARATOR}${checksum(body)}`;
}

/**
 * Reads a cursor in the shape `encodeOnlineCursor` writes. The checksum catches a cursor damaged by accident. It is
 * not a signature: a cursor written by hand for any place parses, so the caller treats the place as untrusted.
 *
 * @param text The cursor as sent.
 * @returns The place it names, or null for text longer than `ONLINE_CURSOR_MAX_CHARS`, of another shape or another
 *   version, with a checksum that does not hold or with a field that does not unescape.
 */
export function parseOnlineCursor(text: string): OnlinePlace | null {
  if (text.length > ONLINE_CURSOR_MAX_CHARS) {
    return null;
  }
  const fields = text.split(CURSOR_SEPARATOR);
  if (fields.length !== CURSOR_FIELDS || fields[0] !== CURSOR_VERSION) {
    return null;
  }
  const sum = fields[CURSOR_FIELDS - 1] as string;
  const body = text.slice(0, text.length - sum.length - CURSOR_SEPARATOR.length);
  if (!CHECKSUM_PATTERN.test(sum) || checksum(body) !== sum) {
    return null;
  }
  try {
    const [mapId, mapName, first, family] = fields.slice(1, CURSOR_FIELDS - 1).map((field) => decodeURIComponent(field));
    return { mapId: mapId as string, mapName: mapName as string, first: first as string, family: family as string };
  } catch {
    return null;
  }
}
